import React, {useCallback, useEffect, useRef, useState} from 'react';
import {MuseumCarouselProps} from '../types/artwork';
import {museumArtworks} from '../data/museum-artworks';
import {FaMapLocationDot, FaArrowRight, FaPlay, FaPause} from "react-icons/fa6";

const MuseumCarousel: React.FC<MuseumCarouselProps> = ({
                                                           artworks,
                                                           autoAdvanceDelay = 7000,
                                                           showNavigation = true,
                                                       }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [isPaused, setIsPaused] = useState(true);
    const [autoAdvanceStarted, setAutoAdvanceStarted] = useState(false);
    const [showPauseIcon, setShowPauseIcon] = useState(false);
    const pauseIconTimerRef = useRef<NodeJS.Timeout | null>(null);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isProgrammaticScroll = useRef(false);
    const hasManuallyNavigated = useRef(false);
    const touchStartInfo = useRef<{ x: number; time: number; scrollLeft: number; } | null>(null);

    const scrollToArtwork = useCallback((index: number) => {
        if (scrollContainerRef.current) {
            const itemWidth = scrollContainerRef.current.scrollWidth / artworks.length;
            scrollContainerRef.current.scrollTo({
                left: itemWidth * index,
                behavior: 'smooth'
            });
        }
    }, [artworks.length]);

    // Navigate to specific artwork
    const navigateToArtwork = useCallback((index: number) => {
        // If user navigates away from the first slide, start auto-advance
        if (activeIndex === 0 && index > 0 && !hasManuallyNavigated.current) {
            setAutoAdvanceStarted(true);
            setIsPaused(false);
            hasManuallyNavigated.current = true;
        }
        if (scrollContainerRef.current) {
            isProgrammaticScroll.current = true;
            setActiveIndex(index);
            scrollToArtwork(index);
        }
    }, [scrollToArtwork, activeIndex, hasManuallyNavigated]);

    // Function to toggle pause state and show the icon
    const togglePause = useCallback(() => {
        if (autoAdvanceStarted){
            setIsPaused(prev => !prev);
            setShowPauseIcon(true);
            if (pauseIconTimerRef.current) clearTimeout(pauseIconTimerRef.current);
            pauseIconTimerRef.current = setTimeout(() => setShowPauseIcon(false), 1000);
        }
    }, [autoAdvanceStarted]);

    // Auto-advance functionality
    useEffect(() => {
        if (autoAdvanceStarted && !isPaused && !isDragging) {
            autoAdvanceTimerRef.current = setTimeout(() => {
                setActiveIndex((prev) => (prev + 1) % artworks.length);
            }, autoAdvanceDelay);
        }

        return () => {
            if (autoAdvanceTimerRef.current) {
                clearTimeout(autoAdvanceTimerRef.current);
            }
        };
    }, [activeIndex, autoAdvanceDelay, isPaused, isDragging, artworks.length, autoAdvanceStarted]);

    // Sync scroll position when activeIndex changes (for auto-advance)
    useEffect(() => {
        if (!isDragging) {
            navigateToArtwork(activeIndex);
        }
    }, [activeIndex, isDragging, navigateToArtwork]);

    // Handle scroll position updates
    const handleScroll = useCallback(() => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            if (isDragging || !scrollContainerRef.current) return;

            if (isProgrammaticScroll.current) {
                isProgrammaticScroll.current = false;
                return;
            }
            const {scrollLeft, scrollWidth} = scrollContainerRef.current;
            const itemWidth = scrollWidth / artworks.length;
            setActiveIndex(Math.round(scrollLeft / itemWidth));
        }, 150); // Debounce to run after scrolling stops
    }, [isDragging, artworks.length]);

    // Setup scroll listener
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, {passive: true});
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // Mouse drag handlers
    const onMouseDown = (e: React.MouseEvent) => {
        if (scrollContainerRef.current && e.button === 0) {
            setIsDragging(true);
            setIsPaused(true);
            setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
            setScrollLeft(scrollContainerRef.current.scrollLeft);
            scrollContainerRef.current.style.cursor = 'grabbing';
            scrollContainerRef.current.style.scrollSnapType = 'none';
        }
    };

    // Mouse move and up handlers
    useEffect(() => {
        const container = scrollContainerRef.current;

        const onMouseMove = (e: MouseEvent) => {
            if (!isDragging || !container) return;
            e.preventDefault();
            const x = e.pageX - container.offsetLeft;
            const walk = (x - startX);
            container.scrollLeft = scrollLeft - walk;
        };

        const onMouseUp = () => {
            if (isDragging && container) {
                setIsDragging(false);

                const itemWidth = container.scrollWidth / artworks.length;
                let newIndex = Math.round(container.scrollLeft / itemWidth);

                // Force snap back if over-scrolled
                if (newIndex < 0) {
                    newIndex = 0;
                } else if (newIndex >= artworks.length) {
                    newIndex = artworks.length - 1;
                }

                const clampedIndex = Math.max(0, Math.min(newIndex, artworks.length - 1));

                setActiveIndex(clampedIndex);
                scrollToArtwork(clampedIndex);
                container.style.cursor = 'grab';
                container.style.scrollSnapType = 'x mandatory';
            }
        };

        if (isDragging) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging, startX, scrollLeft, artworks.length, scrollToArtwork]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.target && (e.target as HTMLElement).tagName === 'INPUT') return;

        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                navigateToArtwork((activeIndex - 1 + artworks.length) % artworks.length);
                break;
            case 'ArrowRight':
                e.preventDefault();
                navigateToArtwork((activeIndex + 1) % artworks.length);
                break;
            case ' ':
            case 'Spacebar':
                e.preventDefault();
                togglePause();
                break;
            case 'Home':
                e.preventDefault();
                navigateToArtwork(0);
                break;
            case 'End':
                e.preventDefault();
                navigateToArtwork(artworks.length - 1);
                break;
        }
    }, [activeIndex, artworks.length, navigateToArtwork, togglePause]);

    // Setup keyboard event listener
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (pauseIconTimerRef.current) clearTimeout(pauseIconTimerRef.current);
        };
    }, []);

    const currentArtwork = artworks[activeIndex];

    return (
        <section className="relative z-10 py-20 px-4 md:px-6 bg-[url(assets/images/corcho.jpg)] bg-cover bg-center h-lvh md:h-lvh">
            {/* Dot Indicators */}
            {showNavigation && (
                <div className="flex justify-center space-x-2 mb-8 md:mt-0">
                    {artworks.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => {
                                navigateToArtwork(index);
                                // Manually pausing when user interacts with dots
                                togglePause();
                            }}
                            className={`relative h-3 rounded-full transition-all duration-300 overflow-hidden ${
                                activeIndex === index ? 'w-6 bg-(--primary-color)/80' : 'w-3 bg-gray-400 hover:bg-white'
                            }`}
                            aria-label={`Go to artwork ${index + 1}`}
                        >
                            {/* Visual Timer Progress Bar */}
                            {activeIndex === index && autoAdvanceStarted && (
                                <div
                                    key={activeIndex} // Re-trigger animation on index change
                                    className={`absolute top-0 left-0 h-full bg-[var(--hover-color)] ${
                                        isPaused ? 'progress-bar-paused' : 'progress-bar-animate'
                                    }`}
                                    style={{'--auto-advance-delay': `${autoAdvanceDelay / 1000}s`} as React.CSSProperties}
                                />
                            )}
                        </button>
                    ))}
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-8 lg: gap-2 h-[80lvh]">
                <div className="mb-6 lg:mb-0 lg:flex-1 row-span-12 lg:col-span-4">
                    <div className="relative mx-auto max-w-full">
                        <div
                            ref={scrollContainerRef}
                            className="flex overflow-x-auto scrollbar-hide cursor-grab relative"
                            style={{scrollSnapType: 'x mandatory'}}
                            onMouseDown={onMouseDown}
                            onClick={togglePause}
                            onTouchStart={(e) => {
                                const touch = e.touches[0];
                                if (scrollContainerRef.current && touch) {
                                    touchStartInfo.current = {
                                        x: touch.clientX,
                                        time: Date.now(),
                                        scrollLeft: scrollContainerRef.current.scrollLeft
                                    };
                                    setIsDragging(true);
                                    setIsPaused(true);
                                    setStartX(touch.clientX - scrollContainerRef.current.offsetLeft);
                                    setScrollLeft(scrollContainerRef.current.scrollLeft);
                                    scrollContainerRef.current.style.scrollSnapType = 'none';
                                }
                            }}
                            onTouchMove={(e) => {
                                if (!isDragging || !scrollContainerRef.current) return;
                                e.preventDefault();
                                const touch = e.touches[0];
                                const x = touch.clientX - scrollContainerRef.current.offsetLeft;
                                const walk = (x - startX);
                                scrollContainerRef.current.scrollLeft = scrollLeft - walk;
                            }}
                            onTouchEnd={() => {
                                if (isDragging && scrollContainerRef.current) {
                                    setIsDragging(false);
                                    // Don't reset isPaused here, let the auto-advance timer handle it if needed.

                                    const FLICK_VELOCITY_THRESHOLD = 0.5; // pixels/ms
                                    const FLICK_DISTANCE_THRESHOLD = 50; // pixels

                                    let newIndex = activeIndex;

                                    if (touchStartInfo.current) {
                                        const deltaTime = Date.now() - touchStartInfo.current.time;
                                        const deltaX = scrollContainerRef.current.scrollLeft - touchStartInfo.current.scrollLeft;
                                        const velocity = Math.abs(deltaX) / deltaTime;

                                        if (velocity > FLICK_VELOCITY_THRESHOLD && Math.abs(deltaX) > FLICK_DISTANCE_THRESHOLD) {
                                            // It's a flick
                                            if (deltaX > 0) { // Flick to next
                                                newIndex = Math.min(activeIndex + 1, artworks.length - 1);
                                            } else { // Flick to previous
                                                newIndex = Math.max(activeIndex - 1, 0);
                                            }
                                        } else {
                                            // It's a slow drag, use position-based snapping
                                            const itemWidth = scrollContainerRef.current.scrollWidth / artworks.length;
                                            newIndex = Math.round(scrollContainerRef.current.scrollLeft / itemWidth);

                                            // Force snap back if over-scrolled
                                            if (newIndex < 0) {
                                                newIndex = 0;
                                            } else if (newIndex >= artworks.length) {
                                                newIndex = artworks.length - 1;
                                            }
                                        }
                                        touchStartInfo.current = null;
                                    }

                                    const clampedIndex = Math.max(0, Math.min(newIndex, artworks.length - 1));
                                    navigateToArtwork(clampedIndex);

                                    scrollContainerRef.current.style.scrollSnapType = 'x mandatory';
                                }
                            }}
                        >
                            {artworks.map((artwork, _) => (
                                <div
                                    key={artwork.id}
                                    className="flex-shrink-0 w-full relative"
                                    style={{scrollSnapAlign: 'start'}}
                                >
                                    <div className="relative">
                                        <img
                                            src={artwork.imageUrl}
                                            alt={`${artwork.title}, ${artwork.year}`}
                                            className="w-full h-full md:h-80 lg:h-[70vh] object-fill object-center transition-all duration-500"
                                            onDragStart={(e) => e.preventDefault()}
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = `https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=800&h=600&fit=crop&crop=center`;
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Pause/Play Icon Overlay */}
                        <div
                            className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 pointer-events-none ${
                                showPauseIcon ? 'opacity-100' : 'opacity-0'
                            }`}
                        >
                            {isPaused ? (
                                <div className="drop-shadow-lg">
                                    {FaPlay({size:48, color:"white"})}
                                </div>
                            ) : (
                                <div className="drop-shadow-lg">
                                    {FaPause({size:48, color:"white"})}
                                </div>
                            )}
                        </div>


                    </div>
                </div>

                {currentArtwork.id === '0' && (
                    <div className="relative inset-0 flex justify-center animate-bounce-x md:absolute md:top-[55svh] md:left-[15%]">
                        {FaArrowRight({size: 40, color: 'white'})}
                    </div>
                )}
                {/* Artwork Information */}
                <div className="text-center lg:text-left mb-6 px-2 grid grid-rows-subgrid row-span-12 lg:col-span-4 md:ml-20 md:mr-20">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white row-start-4">
                        {currentArtwork.title}
                    </h2>
                    <p className="text-lg md:text-xl lg:text-2xl text-white font-medium row-start-5">
                        {currentArtwork.year}
                    </p>
                    <p className="text-sm md:text-base lg:text-lg text-white leading-relaxed text-justify lg:text-left row-start-6 row-span-2">
                        {currentArtwork.description}
                    </p>
                    <div className="grid grid-cols-4 text-xs lg:text-sm text-white row-start-8">
                        <p className="col-start-3 md:col-span-1 col-span-2">{currentArtwork.location}</p>
                        {(currentArtwork.locationUrl !== '') ? (
                            <button className="hover:cursor-pointer col-start-4 " aria-label="Ver en mapa" onClick={() => {
                                window.open(currentArtwork.locationUrl)
                            }}>{FaMapLocationDot({size: 24, color: 'white'})}</button>
                        ) : (<></>)}
                    </div>
                </div>
            </div>
        </section>
    );
};

// Default export using the sample data
const AboutUs: React.FC = () => {
    return <MuseumCarousel artworks={museumArtworks} autoAdvance={true}/>;
};

export default AboutUs;